import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  updateProfile as firebaseUpdateProfile,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { User } from '../types/user';

const googleProvider = new GoogleAuthProvider();

export const authService = {
  // Sign up with email and password
  async signUp(email: string, password: string, displayName: string): Promise<User> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Update Firebase Auth profile
    await firebaseUpdateProfile(firebaseUser, {
      displayName: displayName
    });

    // Create user document in Firestore
    const userData: User = {
      id: firebaseUser.uid,
      displayName: displayName,
      email: email,
      role: 'user', // Default role
      createdAt: new Date(),
      favoriteBooks: [],
      ...(firebaseUser.photoURL && { photoURL: firebaseUser.photoURL })
    };

    await setDoc(doc(db, 'users', firebaseUser.uid), userData);
    return userData;
  },

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<User> {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;
    
    return await this.getUserData(firebaseUser.uid);
  },

  // Sign in with Google
  async signInWithGoogle(): Promise<User> {
    const userCredential = await signInWithPopup(auth, googleProvider);
    const firebaseUser = userCredential.user;

    // Check if user exists in Firestore
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    
    if (!userDoc.exists()) {
      // Create new user document
      const userData: User = {
        id: firebaseUser.uid,
        displayName: firebaseUser.displayName || 'Anonymous User',
        email: firebaseUser.email || '',
        role: 'user',
        createdAt: new Date(),
        favoriteBooks: [],
        ...(firebaseUser.photoURL && { photoURL: firebaseUser.photoURL })
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), userData);
      return userData;
    }

    return userDoc.data() as User;
  },

  // Sign out
  async signOut(): Promise<void> {
    await firebaseSignOut(auth);
  },

  // Get user data from Firestore
  async getUserData(uid: string): Promise<User> {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }
    return userDoc.data() as User;
  },

  // Update user profile
  async updateProfile(uid: string, data: Partial<User>): Promise<void> {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...data,
      updatedAt: new Date()
    });
  },

  // Convert Firebase User to our User type
  async convertFirebaseUser(firebaseUser: FirebaseUser): Promise<User> {
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    
    if (userDoc.exists()) {
      return userDoc.data() as User;
    }
    
    // If user document doesn't exist, create one
    const userData: User = {
      id: firebaseUser.uid,
      displayName: firebaseUser.displayName || 'Anonymous User',
      email: firebaseUser.email || '',
      role: 'user',
      createdAt: new Date(),
      favoriteBooks: [],
      ...(firebaseUser.photoURL && { photoURL: firebaseUser.photoURL })
    };

    await setDoc(doc(db, 'users', firebaseUser.uid), userData);
    return userData;
  }
}; 